import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { MessageThread } from '@/modules/messaging/components/messageThread/MessageThread';
import { ThreadHeader } from '@/modules/messaging/components/messageThread/ThreadHeader';
import { MessagingLayout } from '@/modules/messaging/components/MessagingLayout';
import { ThreadList } from '@/modules/messaging/components/threadList/ThreadList';
import { messagingSections } from '@/modules/messaging/constants';
import { useMessageThreadQuery } from '@/modules/messaging/queries/useMessageThreadQuery';
import { useMessageThreadsQuery } from '@/modules/messaging/queries/useMessageThreadsQuery';
import { useDebouncedValue } from '@/modules/search/hooks/useDebouncedValue';
import { Button } from '@/modules/ui/components/button/Button';
import { CardEmptyState } from '@/modules/ui/components/card-empty-state/CardEmptyState';
import { Input } from '@/modules/ui/components/input/Input';

export const Conversations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedThreadId = searchParams.get('threadId');
  const tab = searchParams.get('tab') ?? messagingSections.conversations;
  const [search, setSearch] = useState('');
  const [searchKeyword, setSearchKeyword] = useDebouncedValue(
    search,
    setSearch,
  );
  const messageThreadsQuery = useMessageThreadsQuery(search.trim());
  const { threads } = messageThreadsQuery;
  const listedThread = selectedThreadId
    ? threads.find((thread) => thread.id === selectedThreadId)
    : threads.at(0);
  // Notification links can point beyond the loaded pages or current search.
  const linkedThreadQuery = useMessageThreadQuery(
    listedThread ? null : selectedThreadId,
  );
  const selectedThread = listedThread ?? linkedThreadQuery.data;

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="border-b px-6 py-4">
        <Input
          type="text"
          aria-label="Search by candidate name"
          placeholder="Search by candidate name..."
          value={searchKeyword}
          autoFocus
          onChange={(event) => setSearchKeyword(event.target.value)}
        />
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {messageThreadsQuery.isError && !messageThreadsQuery.data ? (
          <CardEmptyState title="Conversations could not be loaded. Please refresh the page." />
        ) : (
          <ThreadList
            threads={threads}
            isLoading={messageThreadsQuery.isLoading}
            selectedThreadId={selectedThread?.id ?? null}
            onThreadSelect={(threadId) => {
              setSearchParams({ tab, threadId });
            }}
          />
        )}
        {messageThreadsQuery.isFetchNextPageError && (
          <p role="alert" className="text-muted-foreground px-6 py-3 text-sm">
            More conversations could not be loaded. Please refresh the page.
          </p>
        )}
        {messageThreadsQuery.hasNextPage && (
          <div className="p-4">
            <Button
              variant="outline"
              className="w-full"
              loading={messageThreadsQuery.isFetchingNextPage}
              disabled={messageThreadsQuery.isFetching}
              onClick={() => messageThreadsQuery.fetchNextPage()}
            >
              Load more conversations
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const content = selectedThread ? (
    <>
      <ThreadHeader thread={selectedThread} />
      <MessageThread key={selectedThread.id} threadId={selectedThread.id} />
    </>
  ) : (
    <CardEmptyState
      title={
        linkedThreadQuery.isError
          ? 'This conversation could not be loaded. Select another conversation or refresh the page.'
          : messageThreadsQuery.isLoading || linkedThreadQuery.isLoading
            ? 'Loading conversation...'
            : 'Select a conversation to start messaging'
      }
    />
  );

  return (
    <MessagingLayout
      sidebar={sidebar}
      sidebarTitle="Conversations"
      content={content}
    />
  );
};
